import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { UserRole, type AuthUser } from '@/types';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ThemeToggle';
import { QuickLembretesButton } from '@/components/layout/QuickLembretesButton';

const ROLE_LABEL: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Administrador',
  [UserRole.GESTOR]: 'Gestor',
  [UserRole.OPERADOR]: 'Operador',
  [UserRole.OFICINA]: 'Oficina',
};

function getInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[1][0] || '')).toUpperCase();
}

function resolveUserAvatarUrl(avatarUrl: string | null | undefined): string | null {
  if (!avatarUrl?.trim()) return null;
  const path = avatarUrl.trim();
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const env = (import.meta as unknown as { env?: { VITE_API_STATIC_ORIGIN?: string } }).env;
  const staticOrigin =
    typeof env?.VITE_API_STATIC_ORIGIN === 'string' && env.VITE_API_STATIC_ORIGIN
      ? env.VITE_API_STATIC_ORIGIN.replace(/\/$/, '')
      : typeof window !== 'undefined'
        ? window.location.origin
        : '';
  if (!staticOrigin) return null;
  return `${staticOrigin}${path.startsWith('/') ? path : `/${path}`}`;
}

function UserAvatarButton({
  user,
  open,
  onClick,
  id,
}: {
  user: AuthUser;
  open: boolean;
  onClick: () => void;
  id: string;
}) {
  const [imgError, setImgError] = useState(false);
  const src = !imgError && user.avatarUrl ? resolveUserAvatarUrl(user.avatarUrl) : null;
  const initials = getInitials(user.name);

  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        'relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full',
        'ring-2 ring-border transition-shadow',
        'bg-accent/20 text-sm font-semibold text-foreground',
        'dark:bg-accent/30 dark:text-foreground',
        open && 'ring-accent/50',
        'hover:ring-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-background'
      )}
      title="Perfil"
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </button>
  );
}

export const PortalTopbar: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonId = 'portal-profile-trigger';

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!user) return null;

  const handleLogout = () => {
    close();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/80 bg-card/95 shadow-sm backdrop-blur-md dark:bg-card/90 dark:shadow-[0_1px_0_0] dark:shadow-black/20">
      <div className="flex w-full items-center justify-between gap-2 px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-medium uppercase tracking-[0.1em] text-muted-foreground sm:text-[10px]">Zeh Motoca</p>
          <h1 className="font-heading text-base font-bold tracking-tight text-foreground sm:text-lg">PortalFly</h1>
          <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground sm:text-xs">Escolha um módulo abaixo.</p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2" ref={rootRef}>
          <ThemeToggle />
          <QuickLembretesButton />
          <div className="relative">
            <UserAvatarButton
              id={buttonId}
              user={user}
              open={open}
              onClick={() => setOpen((v) => !v)}
            />
            {open && (
              <div
                role="menu"
                aria-labelledby={buttonId}
                className={cn(
                  'absolute right-0 z-50 mt-2 w-[min(100vw-2rem,18rem)]',
                  'rounded-lg border border-border bg-popover p-0 text-popover-foreground shadow-lg',
                  'ring-1 ring-border/50 dark:ring-white/5'
                )}
              >
                <div className="border-b border-border/80 px-3 py-3">
                  <div className="flex gap-3">
                    <div
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full',
                        'bg-accent/20 text-sm font-semibold text-foreground dark:bg-accent/30'
                      )}
                    >
                      <AvatarInPopover name={user.name} avatarUrl={user.avatarUrl} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground" title={user.name}>
                        {user.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground" title={user.email}>
                        {user.email}
                      </p>
                      <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        <UserIcon className="h-3 w-3" aria-hidden />
                        {ROLE_LABEL[user.role] ?? user.role}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-1.5">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

function AvatarInPopover({ name, avatarUrl }: { name: string; avatarUrl: string | null | undefined }) {
  const [imgError, setImgError] = useState(false);
  const src = !imgError && avatarUrl ? resolveUserAvatarUrl(avatarUrl) : null;
  const initials = getInitials(name);
  if (src) {
    return (
      <img src={src} alt="" className="h-full w-full object-cover" onError={() => setImgError(true)} />
    );
  }
  return <span className="select-none">{initials}</span>;
}
